// The consumer home's sidebar column: "Your Care Team", plus the Appointments tile the host passes
// in. The host renders this into `PageLayout variant="sidebar"`, which owns the 66/33 split and the
// collapse to a single column below `lg`.
//
// "Your Care Team" is `@internal/ui`'s `RailTile` at `color="blue"`, whose colored top bar is
// exactly the design's rule: `blue` resolves to `brandBlue[200]`, `primary` to `primary.light`.
//
// The care-team member is the member's real Carrot Companion since CONAPP-294. Only the role label
// is still fixed — see the comment at it.
//
// ⚠ THE APPOINTMENTS TILE NO LONGER LIVES HERE, and this file no longer reads appointments. MEM-856
// replaced CONAPP-295's inline tile with `@internal/telemedicine`'s `HomeAppointmentsTile`, injected
// through the `appointmentsTile` slot: that package owns the avatar, formatting and imminence rule the
// comps' states need, none of it reachable from here.
//
// ⚠ THE TILE AND THE PLAN CARD BESIDE IT ARE ONE ANSWER. Both read `telemedicine.getAppointments`
// under the shared `APPOINTMENTS_QUERY_KEY` (`@internal/constants/telemedicine/appointments`), so
// one request answers both and neither can show a visit the other has dropped. Do not give either
// surface a read of its own — that is what broke CONAPP-295's "the tile and the card never disagree
// about whether a visit is booked" guarantee once already.
//
// COPY: the content doc has no sidebar row, so every string here is AUTHORED rather than quoted.

import { Avatar, Button, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import { RailTile, Skeleton } from "@internal/ui"
import React from "react"
import { FormattedMessage } from "react-intl"
import { Link as ReactRouterLink } from "react-router-dom"
import { MESSAGES_PATH } from "./paths"

/**
 * The member's Carrot Companion, as the host hands it over.
 *
 * A prop rather than a hook inside this package: the host resolves it with `useCarrotCompanion`
 * (which reads the monolithic `carrotClient` and the host's user context) and derives both fields
 * with `formatCarrotCompanionName` — the helper every other care-team surface uses. Neither is
 * reachable from here (one-way dependency — see packages/consumer/README.md), and the fields arrive
 * pre-formatted so this tile cannot render a companion differently from the rest of the app.
 *
 * Deliberately NOT `@internal/telemedicine`'s `AppointmentsCompanion`: that one carries
 * `{ name, photoUrl, displayName }` and no initials, because the Appointments page renders only a
 * name where this tile renders an initials `Avatar`. `photoUrl` is available from the same hook if
 * design ever wants a photo here; today's design is initials.
 */
export type ConsumerCareTeamMember = {
  displayName: string
  initials: string
}

interface ConsumerCarrotPlanSidebarProps {
  /** `null` while the host's query is in flight, and for a member with no companion assigned. */
  careTeamMember: ConsumerCareTeamMember | null
  careTeamMemberLoading?: boolean
  /**
   * The Appointments tile, or a falsy value when the member shouldn't see one.
   *
   * A slot rather than the `showTelemedicine` boolean it replaced: injecting the tile keeps this
   * package free of a dependency on `@internal/telemedicine`, and keeps the eligibility gate in the
   * host (`src/pages/consumer/home/page.tsx`).
   */
  appointmentsTile: React.ReactNode
}

export function ConsumerCarrotPlanSidebar({
  careTeamMember,
  careTeamMemberLoading = false,
  appointmentsTile
}: ConsumerCarrotPlanSidebarProps): JSX.Element {
  const theme = useTheme()

  return (
    <Stack spacing={theme.spacing(theme.tokens.spacing.lg)}>
      {/* The card's padding and internal rhythm belong to `RailTile`, which the Appointments tile
          in the slot below draws from too — see the note on it for why they are shared. */}
      <RailTile color="blue">
        <Typography variant="h5" component="h2" color="text.primary">
          <FormattedMessage defaultMessage="Your Care Team" />
        </Typography>
        {!careTeamMember && careTeamMemberLoading && (
          <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.sm)} sx={{ alignItems: "center" }}>
            <Skeleton width={40} height={40} />
            <Stack spacing={theme.spacing(theme.tokens.spacing.xxs)}>
              <Skeleton width={112} height={18} />
              <Skeleton width={128} height={14} />
            </Stack>
          </Stack>
        )}
        {/* No companion assigned: the whole avatar-and-name block goes, rather than an empty
            avatar or a hidden tile — messaging is available either way, so the heading, the
            description and the CTA below all stand. */}
        {careTeamMember && (
          <Stack direction="row" spacing={theme.spacing(theme.tokens.spacing.sm)} sx={{ alignItems: "center" }}>
            <Avatar
              variant="rounded"
              sx={(theme) => ({
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.secondary.contrastText
              })}
            >
              {careTeamMember.initials}
            </Avatar>
            <Stack>
              <Typography variant="h6" component="p" color="text.primary">
                {careTeamMember.displayName}
              </Typography>
              {/* The NAME is real; only the ROLE is fixed. There is no care-team roster read
                  anywhere in the app, so the nurse and REI roles the archetype stacks carry have
                  no source to label themselves from. */}
              <Typography variant="body2">
                <FormattedMessage defaultMessage="Your Carrot Companion" />
              </Typography>
            </Stack>
          </Stack>
        )}
        <Typography variant="body1">
          <FormattedMessage defaultMessage="Here to help with your plan, appointments, services, and next steps." />
        </Typography>
        <Button
          variant="outlined"
          color="secondary"
          component={ReactRouterLink}
          to={MESSAGES_PATH}
          sx={{ alignSelf: "flex-start" }}
        >
          <FormattedMessage defaultMessage="Send a message" />
        </Button>
      </RailTile>

      {appointmentsTile}
    </Stack>
  )
}
